// Tenky fetch wrapper pro vsechny volani na backend.
//
// - Vsechny zadosti jdou na /api/... a posilaji cookies (`credentials: 'include'`).
// - Na 401 vyhlasime globalni CustomEvent('auth:unauthorized'), na ktery
//   reaguje AuthContext (vynuluje uzivatele a presmeruje na /login).
// - Na ne-2xx vyhodi `ApiError` se statusem a pokud server vrati JSON
//   `{ error, message? }`, naparsujeme to.

import type { ApiErrorBody } from '@/types/api';

const BASE = '/api';

/** Nazev eventu, ktery posila api wrapper na window pri 401 odpovedi. */
export const UNAUTHORIZED_EVENT = 'auth:unauthorized';

export class ApiError extends Error {
  public readonly status: number;
  public readonly path: string;
  public readonly body: ApiErrorBody | string | null;

  constructor(status: number, path: string, message: string, body: ApiErrorBody | string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.body = body;
  }
}

/**
 * Univerzalni JSON fetch.
 *
 * - `path` musi zacinat lomitkem (napr. `/mechanizace`).
 * - Pokud `init.body` neni string, autoamticky se zaserializuje a doplni hlavicka Content-Type.
 * - Na 204/205 vraci `undefined as T`.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');

  let body = init?.body;
  if (body !== undefined && body !== null && !(body instanceof FormData) && !(body instanceof Blob) && !(body instanceof ArrayBuffer)) {
    // Auto-serializace objektu na JSON; stringy nechame projit. V obou pripadech
    // ale nastavime Content-Type: application/json, pokud caller neurcil jinak —
    // jinak ASP.NET Core [FromBody] vrati 415 Unsupported Media Type.
    if (typeof body !== 'string') {
      body = JSON.stringify(body);
    }
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    body,
    headers,
    credentials: 'include',
  });

  if (res.status === 401) {
    // Notifikuj AuthContext (a kohokoli, kdo poslouchá) o invalidni / expirovane session.
    try {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { path } }));
    } catch {
      // ignore (SSR / non-browser kontext)
    }
  }

  if (!res.ok) {
    let parsed: ApiErrorBody | string | null = null;
    let message = `API ${path} failed (${res.status} ${res.statusText})`;
    const contentType = res.headers.get('content-type') ?? '';
    try {
      if (contentType.includes('application/json')) {
        parsed = (await res.json()) as ApiErrorBody;
        // Backend vraci bud { error, message }, nebo ProblemDetails { title, detail }
        // (DomainExceptionFilter) — vezmi prvni lidsky citelnou hodnotu.
        if (parsed?.message) message = parsed.message;
        else if (parsed?.detail) message = parsed.detail;
        else if (parsed?.error) message = parsed.error;
      } else {
        parsed = await res.text();
        if (parsed) message = `${message}: ${parsed}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new ApiError(res.status, path, message, parsed);
  }

  if (res.status === 204 || res.status === 205) {
    return undefined as T;
  }

  // Pokud server vrati 200 bez tela, fetch().json() by hodil; ochrana:
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
